import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatPrice, pricePerSqft, typeLabel, localityOf } from '@/lib/format';
import { calcEMI, calcStampDuty, calcRentalYield, calcAffordability, STAMP_DUTY, stateKeyFrom, inr } from '@/lib/calc';
import {
  Calculator, Coins, Receipt, TrendingUp, BarChart3, Scale, Compass, ClipboardList, Building2, Store, ArrowRight,
} from 'lucide-react';

const Field = ({ label, children, hint }) => (
  <div><div className="flex items-center justify-between mb-1.5"><span className="text-sm text-slate-600">{label}</span>{hint && <span className="text-sm font-semibold text-[#0F172A]">{hint}</span>}</div>{children}</div>
);

const Row = ({ k, v, strong }) => (
  <div className={`flex items-center justify-between py-2 border-b border-slate-100 text-sm ${strong ? 'font-bold text-[#0F172A]' : ''}`}><span className="text-slate-500">{k}</span><span className={strong ? '' : 'font-semibold text-[#0F172A]'}>{v}</span></div>
);

// ---------- Inline EMI ----------
const EmiTool = ({ price }) => {
  const [value, setValue] = useState(price || 5000000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const down = Math.round((value * downPct) / 100);
  const loan = Math.max(0, value - down);
  const res = useMemo(() => calcEMI(loan, rate, years * 12), [loan, rate, years]);
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="inline-emi">
      <div className="space-y-4">
        <Field label="Property price" hint={inr(value)}><Input type="number" value={value} onChange={(e) => setValue(+e.target.value)} className="rounded-xl" data-testid="emi-price-input" /></Field>
        <Field label="Down payment" hint={`${downPct}% · ${inr(down)}`}><Slider value={[downPct]} onValueChange={([v]) => setDownPct(v)} min={0} max={90} step={5} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
        <Field label="Loan amount" hint={inr(loan)}><div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#0F172A]" style={{ width: `${value ? (loan / value) * 100 : 0}%` }} /></div></Field>
        <Field label="Interest rate (p.a.)" hint={`${rate}%`}><Slider value={[rate]} onValueChange={([v]) => setRate(+v.toFixed(1))} min={5} max={20} step={0.1} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
        <Field label="Tenure" hint={`${years} years`}><Slider value={[years]} onValueChange={([v]) => setYears(v)} min={1} max={30} step={1} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
      </div>
      <div className="bg-[#0F172A] rounded-2xl p-6 text-white flex flex-col justify-center">
        <p className="text-xs uppercase tracking-widest text-white/60">Monthly EMI</p>
        <p className="text-4xl font-bold text-[#D4AF37] tabular-nums mt-1" data-testid="inline-emi-result">₹{res.emi.toLocaleString('en-IN')}</p>
        <div className="mt-5 space-y-0.5 text-white/90">
          <div className="flex justify-between text-sm py-1.5 border-b border-white/10"><span className="text-white/60">Loan amount</span><span className="font-semibold">{inr(loan)}</span></div>
          <div className="flex justify-between text-sm py-1.5 border-b border-white/10"><span className="text-white/60">Total interest</span><span className="font-semibold">{inr(res.totalInterest)}</span></div>
          <div className="flex justify-between text-sm py-1.5"><span className="text-white/60">Total payable</span><span className="font-semibold">{inr(res.totalAmount)}</span></div>
        </div>
      </div>
    </div>
  );
};

// ---------- Inline Stamp Duty ----------
const StampTool = ({ price, stateName, propertyType }) => {
  const [value, setValue] = useState(price || 5000000);
  const [stateKey, setStateKey] = useState(stateKeyFrom(stateName));
  const [gender, setGender] = useState('male');
  const res = useMemo(() => calcStampDuty(value, stateKey, gender, propertyType === 'commercial' ? 'commercial' : 'residential'), [value, stateKey, gender, propertyType]);
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="inline-stamp-duty">
      <div className="space-y-4">
        <Field label="Property value" hint={inr(value)}><Input type="number" value={value} onChange={(e) => setValue(+e.target.value)} className="rounded-xl" data-testid="stamp-price-input" /></Field>
        <Field label="State">
          <Select value={stateKey} onValueChange={setStateKey}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(STAMP_DUTY).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
        </Field>
        <Field label="Buyer">
          <Select value={gender} onValueChange={setGender}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="male">Male / Joint</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent></Select>
        </Field>
      </div>
      <div className="bg-[#FBFAF6] border border-slate-200 rounded-2xl p-6">
        <p className="text-xs uppercase tracking-widest text-slate-400">Total payable</p>
        <p className="text-3xl font-bold text-[#0F172A] tabular-nums mt-1" data-testid="inline-stamp-result">{inr(res.total)}</p>
        <p className="text-xs text-slate-500 mt-0.5">{((res.total / value) * 100).toFixed(1)}% of property value</p>
        <div className="mt-4"><Row k={`Stamp duty (${res.stampPct}%)`} v={inr(res.stamp)} /><Row k={`Registration (${res.regPct}%)`} v={inr(res.reg)} /></div>
      </div>
    </div>
  );
};

// ---------- Inline Rental Yield ----------
const RentalTool = ({ rent }) => {
  const [value, setValue] = useState(8000000);
  const [monthlyRent, setMonthlyRent] = useState(rent || 35000);
  const [maintenance, setMaintenance] = useState(5000);
  const [tax, setTax] = useState(15000);
  const [appr, setAppr] = useState(6);
  const [years, setYears] = useState(5);
  const r = useMemo(() => calcRentalYield(value, monthlyRent, maintenance, tax, appr, years), [value, monthlyRent, maintenance, tax, appr, years]);
  const rating = r.netYield >= 4 ? 'Excellent' : r.netYield >= 3 ? 'Good' : r.netYield >= 2 ? 'Fair' : 'Below average';
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="inline-rental-yield">
      <div className="space-y-4">
        <Field label="Property value" hint={inr(value)}><Input type="number" value={value} onChange={(e) => setValue(+e.target.value)} className="rounded-xl" /></Field>
        <Field label="Monthly rent" hint={inr(monthlyRent)}><Input type="number" value={monthlyRent} onChange={(e) => setMonthlyRent(+e.target.value)} className="rounded-xl" data-testid="rental-rent-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Maint./mo"><Input type="number" value={maintenance} onChange={(e) => setMaintenance(+e.target.value)} className="rounded-xl" /></Field>
          <Field label="Tax/yr"><Input type="number" value={tax} onChange={(e) => setTax(+e.target.value)} className="rounded-xl" /></Field>
        </div>
        <Field label="Appreciation p.a." hint={`${appr}%`}><Slider value={[appr]} onValueChange={([v]) => setAppr(v)} min={0} max={15} step={0.5} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
        <Field label="Holding period" hint={`${years} years`}><Slider value={[years]} onValueChange={([v]) => setYears(v)} min={1} max={20} step={1} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
      </div>
      <div className="bg-[#FBFAF6] border border-slate-200 rounded-2xl p-6">
        <p className="text-xs uppercase tracking-widest text-slate-400">Net rental yield</p>
        <p className="text-3xl font-bold text-[#0F172A] tabular-nums mt-1" data-testid="inline-rental-result">{r.netYield.toFixed(2)}% <span className="text-sm font-semibold text-[#B5952F]">{rating}</span></p>
        <div className="mt-4"><Row k="Gross yield" v={`${r.grossYield.toFixed(2)}%`} /><Row k="Net annual income" v={inr(r.netAnnualIncome)} /><Row k={`Total return (${years}y)`} v={inr(r.totalReturn)} /><Row k="ROI" v={`${r.roiPercent.toFixed(1)}%`} strong /></div>
      </div>
    </div>
  );
};

// ---------- Inline Affordability ----------
const AffordTool = () => {
  const [income, setIncome] = useState(100000);
  const [expenses, setExpenses] = useState(40000);
  const [down, setDown] = useState(2000000);
  const [years, setYears] = useState(20);
  const [rate, setRate] = useState(8.5);
  const [ratio, setRatio] = useState(40);
  const r = useMemo(() => calcAffordability(income, expenses, down, years, rate, ratio), [income, expenses, down, years, rate, ratio]);
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="inline-affordability">
      <div className="space-y-4">
        <Field label="Monthly income (in-hand)"><Input type="number" value={income} onChange={(e) => setIncome(+e.target.value)} className="rounded-xl" /></Field>
        <Field label="Monthly fixed expenses"><Input type="number" value={expenses} onChange={(e) => setExpenses(+e.target.value)} className="rounded-xl" /></Field>
        <Field label="Down payment available"><Input type="number" value={down} onChange={(e) => setDown(+e.target.value)} className="rounded-xl" /></Field>
        <Field label="EMI as % of income" hint={`${ratio}%`}><Slider value={[ratio]} onValueChange={([v]) => setRatio(v)} min={20} max={60} step={5} className="[&_[role=slider]]:bg-[#D4AF37]" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tenure (yrs)"><Input type="number" value={years} onChange={(e) => setYears(+e.target.value)} className="rounded-xl" /></Field>
          <Field label="Rate %"><Input type="number" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} className="rounded-xl" /></Field>
        </div>
      </div>
      <div className="bg-[#FBFAF6] border border-slate-200 rounded-2xl p-6">
        <p className="text-xs uppercase tracking-widest text-slate-400">You can afford up to</p>
        <p className="text-3xl font-bold text-[#0F172A] tabular-nums mt-1" data-testid="inline-afford-result">{inr(r.maxProperty)}</p>
        <div className="mt-4"><Row k="Max monthly EMI" v={inr(r.maxEmi)} /><Row k="Eligible loan" v={inr(r.loan)} /><Row k="Monthly surplus" v={inr(r.surplus)} /></div>
        <p className="text-xs text-slate-400 mt-3">Keep EMI under ~40% of income, with a 5–10% buffer for stamp duty & registration.</p>
      </div>
    </div>
  );
};

// ---------- Info panels ----------
const AnalysisPanel = ({ property, rent, psf }) => {
  const rows = [
    ['Asking price', formatPrice(property.price) + (rent ? ' /month' : '')],
    psf && !rent ? ['Price per sq.ft', psf] : null,
    property.area ? ['Area', `${Number(property.area).toLocaleString('en-IN')} ${property.area_unit || 'sqft'}`] : null,
    property.bedrooms ? ['Configuration', `${property.bedrooms} BHK`] : null,
    ['Property type', typeLabel(property.property_type)],
    ['Locality', `${localityOf(property)}, ${property.city}`],
    property.age ? ['Property age', property.age] : null,
    property.furnishing ? ['Furnishing', property.furnishing] : null,
    property.facing ? ['Facing', property.facing] : null,
  ].filter(Boolean);
  return (
    <div data-testid="property-analysis-panel">
      <dl className="grid sm:grid-cols-2 gap-x-6">{rows.map(([k, v]) => <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-[#0F172A] capitalize text-right">{v}</dd></div>)}</dl>
      <p className="text-xs text-slate-400 mt-3">Based on this listing's information. Confirm final terms with the lister.</p>
    </div>
  );
};

const MarketPanel = ({ property, rent, psf }) => (
  <div data-testid="market-analysis-panel">
    <p className="text-sm text-slate-600">{psf && !rent ? `This ${typeLabel(property.property_type).toLowerCase()} is priced at about ${psf} in ${localityOf(property)}, ${property.city}.` : `This listing is in ${localityOf(property)}, ${property.city}.`} For city-level price trends and comparable listings, use the links below.</p>
    <div className="flex flex-wrap gap-2 mt-3">
      <Link to="/market-trends" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] border border-slate-200 rounded-full px-3 py-1.5 hover:border-[#0F172A]"><BarChart3 className="h-3.5 w-3.5" /> City market insights</Link>
      <Link to={`/properties?city=${encodeURIComponent(property.city || '')}&listing_type=${property.listing_type}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] border border-slate-200 rounded-full px-3 py-1.5 hover:border-[#0F172A]"><Building2 className="h-3.5 w-3.5" /> Comparable in {property.city}</Link>
    </div>
    <p className="text-xs text-slate-400 mt-3">Market insights are indicative and may be limited for some localities.</p>
  </div>
);

const VastuPanel = ({ property }) => (
  <div data-testid="vastu-panel">
    {property.facing
      ? <p className="text-sm text-slate-600">This property is <span className="font-semibold capitalize text-[#0F172A]">{property.facing}</span>-facing. Facing is one of several Vastu factors (entrance, room orientation, layout). For a full assessment, consult a verified Vastu expert.</p>
      : <p className="text-sm text-slate-600">Detailed Vastu analysis needs layout/orientation details not available for this listing. Connect with a verified Vastu expert for personalised guidance.</p>}
    <Link to="/professionals/vastu-experts" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] border border-slate-200 rounded-full px-3 py-1.5 mt-3 hover:border-[#0F172A]" data-testid="vastu-consult-link"><Store className="h-3.5 w-3.5" /> Consult a Vastu Expert</Link>
  </div>
);

export const PropertyTools = ({ property }) => {
  const rent = property.listing_type === 'rent';
  const price = property.price || 0;
  const psf = pricePerSqft(property);

  const tabs = rent
    ? [['rental', 'Rental Yield', TrendingUp], ['market', 'Market', BarChart3], ['analysis', 'Analysis', ClipboardList], ['vastu', 'Vastu', Compass]]
    : [['emi', 'EMI', Calculator], ['stamp', 'Stamp Duty', Receipt], ['afford', 'Affordability', Coins], ['market', 'Market', BarChart3], ['analysis', 'Analysis', ClipboardList], ['vastu', 'Vastu', Compass]];
  const [tab, setTab] = useState(tabs[0][0]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="property-tools-section">
      <h2 className="text-xl font-bold text-[#0F172A]">Property Tools & Insights</h2>
      <p className="text-sm text-slate-500 mb-5">Calculate everything right here — pre-filled with this {rent ? 'rental' : 'property'}'s details. No need to leave the page.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="property-tools-grid">
        {tabs.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tool-${id}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${tab === id ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-[#0F172A] border-slate-200 hover:border-[#0F172A]'}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
        <Link to={`/compare?ids=${property.property_id}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border border-slate-200 text-[#0F172A] hover:border-[#0F172A]" data-testid="tool-compare"><Scale className="h-4 w-4" /> Compare <ArrowRight className="h-3 w-3" /></Link>
      </div>

      <div className="min-h-[200px]">
        {tab === 'emi' && <EmiTool price={price} />}
        {tab === 'stamp' && <StampTool price={price} stateName={property.state} propertyType={property.property_type} />}
        {tab === 'afford' && <AffordTool />}
        {tab === 'rental' && <RentalTool rent={price} />}
        {tab === 'market' && <MarketPanel property={property} rent={rent} psf={psf} />}
        {tab === 'analysis' && <AnalysisPanel property={property} rent={rent} psf={psf} />}
        {tab === 'vastu' && <VastuPanel property={property} />}
      </div>
    </div>
  );
};

export default PropertyTools;
